<template>
  <div class="ca-filter-menu">
    <button
      type="button"
      class="ca-filter-menu__trigger"
      :class="{ 'is-open': open, 'has-active': activeCount > 0 }"
      :disabled="disabled"
      aria-haspopup="true"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
      <span>{{ $t('filters') }}</span>
      <span v-if="activeCount > 0" class="ca-filter-menu__badge">{{ activeCount }}</span>
    </button>

    <div v-if="open" class="ca-filter-menu__panel" role="dialog" :aria-label="$t('filters')">
      <div class="ca-filter-menu__body">
        <slot></slot>
      </div>
      <div class="ca-filter-menu__footer">
        <button
          type="button"
          class="ca-filter-menu__clear"
          :disabled="activeCount === 0"
          @click="$emit('clear')"
        >{{ $t('communication_clear_filters') }}</button>
        <button type="button" class="ca-filter-menu__done" @click="close">{{ $t('ca_filters_done') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * "Filters" button that opens a popover holding the filter fields passed in the default slot.
 * The fields' dropdown menus are attached to the page body by vue-material, so clicks inside
 * `.md-menu-content` must not count as clicks outside the popover.
 */
export default {
  props: {
    activeCount: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { open: false }
  },
  watch: {
    disabled(isDisabled) {
      if (isDisabled) this.close()
    },
  },
  beforeDestroy() {
    this.removeListeners()
  },
  methods: {
    toggle() {
      if (this.open) this.close()
      else this.openMenu()
    },
    openMenu() {
      this.open = true
      document.addEventListener('mousedown', this.onPointerDown)
      document.addEventListener('keydown', this.onKeydown)
    },
    close() {
      this.open = false
      this.removeListeners()
    },
    removeListeners() {
      document.removeEventListener('mousedown', this.onPointerDown)
      document.removeEventListener('keydown', this.onKeydown)
    },
    onPointerDown(event) {
      if (this.$el.contains(event.target)) return
      if (event.target.closest && event.target.closest('.md-menu-content')) return
      this.close()
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return
      // Let Escape close an open dropdown first; the popover closes on the next press.
      if (document.querySelector('.md-menu-content')) return
      this.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.ca-filter-menu {
  position: relative;

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid #e1e8ed;
    border-radius: 6px;
    background: #fff;
    color: #2c3e50;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    svg {
      width: 15px;
      height: 15px;
      flex-shrink: 0;
    }

    &:hover:not(:disabled) {
      border-color: #cfd6de;
    }

    &.is-open,
    &.has-active {
      border-color: #2c7be5;
      background: #f3f8ff;
      color: #1f5fb8;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__badge {
    min-width: 18px;
    padding: 1px 5px;
    border-radius: 999px;
    background: #2c7be5;
    color: #fff;
    font-size: 0.68rem;
    line-height: 1.3;
    text-align: center;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    inset-inline-end: 0;
    z-index: 30;
    width: 320px;
    background: #fff;
    border: 1px solid #e1e8ed;
    border-radius: 10px;
    box-shadow: 0 10px 28px rgba(20, 30, 40, 0.16);
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-top: 1px solid #eef0f3;
  }

  &__clear {
    padding: 4px 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #6b7c8f;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
      color: #c0392b;
      background: rgba(192, 57, 43, 0.08);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__done {
    min-height: 30px;
    padding: 0 16px;
    border: 1px solid #2c3e50;
    border-radius: 6px;
    background: #2c3e50;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;
  }
}

@media (max-width: 767px) {
  .ca-filter-menu {
    // The toolbar is the positioning context, so the panel spans it instead of hanging off the button.
    position: static;
    width: 100%;

    &__trigger {
      width: 100%;
      justify-content: center;
    }

    &__panel {
      inset-inline: 0;
      width: auto;
    }
  }
}
</style>
